import _ from "lodash";
import {
  centerOfMass,
  distance,
  length,
  lineSliceAlong,
  point,
  pointToLineDistance,
  round,
  transformRotate
} from "@turf/turf";
import {calculateBearingAngleBetween, getCoordinatesFromSelectedArea} from "./shapeHelper";
import {ProductImageStacType} from "../types/product";
import {
  CoordinatesType,
  LineStringFeatureType,
  PointFeatureType,
  PositionType,
  QuadrilateralCornersType,
  QuadrilateralPerimeterSidesType,
  ShapeFeatureType,
  ShapeType
} from "../types/shape";
import {getCoordinatesFromGeoJSON, getIntersectionShapeFeatureBetweenCoordinates} from "./geoJSONHelper";
import {
  convertToQuadrilateralGeoJSONFrom,
  getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates
} from "./quadrilateralHelper";
import {sortCoordinatesLatASC, sortCoordinatesLatDESC} from "./coordinatesHelper";
import GeoCodingService from "../services/GeoCodingService";

const getIntersectionFeatureBetweenSelectedAreaAndProductImage = (
  selectedArea: ShapeType,
 productImage: ProductImageStacType

): ShapeFeatureType  => {
  const productImageCoords = getCoordinatesFromGeoJSON(productImage);
 const selectedAreaCoords = getCoordinatesFromSelectedArea(selectedArea);
 const intersectionFeature = getIntersectionShapeFeatureBetweenCoordinates(productImageCoords, selectedAreaCoords);

 if (!intersectionFeature) throw new Error("There is not intersection between AoI and a selected image");

 return intersectionFeature;

};

export const doesProductImageIntersectTheSelectedArea = (
  productImage: ProductImageStacType,
  selectedArea: ShapeType
): boolean => {
  const productImageCoords = getCoordinatesFromGeoJSON(productImage);
  const selectedAreaCoords = getCoordinatesFromSelectedArea(selectedArea);
  return productImageCoords && selectedAreaCoords && !!getIntersectionShapeFeatureBetweenCoordinates(productImageCoords, selectedAreaCoords);
};

export const getRotationFeature = (
  shapeFeature: ShapeFeatureType,
  bearingAngle: number,
  centerPoint: PointFeatureType
): ShapeFeatureType => {
  const [pivot] = getCoordinatesFromGeoJSON(centerPoint);
  return transformRotate(shapeFeature, bearingAngle, {
    pivot
  });
};

const getPointRotationPosition = (
  pointPosition: PositionType,
  bearingAngle: number,
  centerPoint: PointFeatureType
): PositionType => {
  const pointFeature = point(pointPosition);
  const rotatedPointFeature = getRotationFeature(pointFeature, bearingAngle, centerPoint);
  return getCoordinatesFromGeoJSON(rotatedPointFeature)[0];
};

const calculateBearingAngleFromLineStringFeature = (
  geoJSONLine: LineStringFeatureType
): number => {
  let angle = 0;
  const coords = getCoordinatesFromGeoJSON(geoJSONLine);
  if (coords && coords.length === 2) {
    const [
    firstPointCoords,
    secondPointCoords
  ] = coords;
    angle = calculateBearingAngleBetween(firstPointCoords, secondPointCoords)
  } else {
    throw new Error("can't be calculated the bearing angle");
  }
  return angle;
};

export const reverseCoordinatesOrder = (
  coords: CoordinatesType
): CoordinatesType => {
  return _.reverse([...coords]);
};

export const getCenterPointFeature = (
  geoJSON: ShapeFeatureType
): PointFeatureType => {
  return centerOfMass(geoJSON);
};

// return the height of scene expressed in Km
const getSceneHeightBy = (
  acquisition_mode: string
): number => {
  return {
    "SPOTLIGHT-2": 10,
    "SPOTLIGHT-2A": 10,
    "SPOTLIGHT-2B": 10,
    "SPOTLIGHT-2C": 10,
    "STRIPMAP": 40,
    "STRIPMAP QUADPOL": 40,
    "QUADPOL": 40,
    "SCANSAR-1": 100,
    "SCANSAR-2": 200,
    "STRIPMAP PINGPONG": 30,
    "PINGPONG": 30,
    "STRIPMAP HIMAGE": 40,
    "SCANSAR WIDE": 100,
    "SCANSAR HUGE": 200
  }[acquisition_mode && acquisition_mode.toUpperCase()] || 0;
};

const calculateSceneHeightBy = (
  product: ProductImageStacType
): number => {
  let sceneHeight = 0;
  if (product && product.properties && product.properties.acquisition_mode) {
    sceneHeight = getSceneHeightBy(product.properties.acquisition_mode);
  }
  if (sceneHeight === 0) throw new Error("It can't be calculate the height of image");
  return sceneHeight;
};

export const calculateProductImageBearingAngle = (product) => {
  const productCoords = getCoordinatesFromGeoJSON(product);
  const {
    left,
    right
  } = getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates(productCoords);
  const line = getLineLength(left) > getLineLength(right)
    ? right
    : left;
  return calculateBearingAngleFromLineStringFeature(line);
};

export const getLineLength = (lineShape: LineStringFeatureType) => {
  return length(lineShape, {units: 'kilometers'});
};

interface DistancesForCutSceneType {
  northDistance: number,
  southDistance: number,
  centerDistance: number,
  productImageHeight: number,
}

function calculateNorthSouthCenterDistances(
  productImageFeature: ShapeFeatureType,
  intersectionFeature: ShapeFeatureType
): DistancesForCutSceneType {
  const intersectionCoordinates = getCoordinatesFromGeoJSON(intersectionFeature);
  const [northPosition] = sortCoordinatesLatDESC(intersectionCoordinates);
  const [southPosition] = sortCoordinatesLatASC(intersectionCoordinates);

  const productCoordinates = getCoordinatesFromGeoJSON(productImageFeature);
  const perimeterLines: QuadrilateralPerimeterSidesType = getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates(productCoordinates);

  const northDistance = round(
    pointToLineDistance(
      point(northPosition),
      perimeterLines.top,
      { units: 'kilometers' }
    )
  , 6);

  const southDistance = round(
    pointToLineDistance(
      point(southPosition),
      perimeterLines.bottom,
      { units: 'kilometers' }
    )
  , 6);

  const [from, to] = getCoordinatesFromGeoJSON(perimeterLines.right);
  const productImageHeight = round(
    distance(
      point(from),
      point(to),
      { units: 'kilometers' }
    )
  , 6);

  const centerDistance = (productImageHeight - northDistance) - southDistance;

  return {
    northDistance,
    southDistance,
    centerDistance,
    productImageHeight
  }
}

interface ScenesInformationType {
  scenes: ShapeFeatureType[],
  sceneCorners?: QuadrilateralCornersType
}

function calculateScenesInformationFromBottomSideBy(
  productImageFeature: ShapeFeatureType,
  intersectionFeature: ShapeFeatureType,
  sceneHeight
): ScenesInformationType {
  const scenesInformation: ScenesInformationType = {
    scenes: []
  };
  const productCoordinates = getCoordinatesFromGeoJSON(productImageFeature);
  const perimeterLines: QuadrilateralPerimeterSidesType = getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates(productCoordinates);

  const distances = calculateDistancesFromBottomSide(productImageFeature, intersectionFeature, sceneHeight);
  for (let i = 1; i < distances.length; i++) {
    const startPointDistance = distances[i - 1];
    const stopPointDistance = distances[i];

    const leftSegment = (
      lineSliceAlong(
        perimeterLines.left,
        startPointDistance,
        stopPointDistance,
        {units: 'kilometers'}
      )
    );

    const rightSegment = (
      lineSliceAlong(
        perimeterLines.right,
        startPointDistance,
        stopPointDistance,
        {units: 'kilometers'}
      )
    );

    if (leftSegment && leftSegment.geometry && rightSegment && rightSegment.geometry) {
      const [
        bottomLeft,
        upLeft
      ] = leftSegment.geometry.coordinates;
      const [
        bottomRight,
        upRight
      ] = rightSegment.geometry.coordinates;

      const scene = getIntersectionShapeFeatureBetweenCoordinates(
        [bottomLeft, upLeft, upRight, bottomRight, bottomLeft],
        productCoordinates
      );

      if (scene) {
        scenesInformation.scenes.push(scene);
        scenesInformation.sceneCorners = {
          bottomLeft: scenesInformation.sceneCorners ? scenesInformation.sceneCorners.bottomLeft : bottomLeft,
          bottomRight: scenesInformation.sceneCorners ? scenesInformation.sceneCorners.bottomRight : bottomRight,
          upLeft,
          upRight,
        }
      }

    }
  }

  return scenesInformation;
}

const getDistances = (
  startBottomDistance: number,
  countScenes: number,
  sceneHeight: number,
  productImageHeight: number,
  isTopCutDirection: boolean
) => {
  const distances: number[] = [startBottomDistance];
  let accumulator = startBottomDistance;
  // last scene could be less than sceneHeight
  const lastSceneHeight = Math.min(sceneHeight, productImageHeight - ((countScenes-1) * sceneHeight));
  for (let index = 1; index <= countScenes; index++) {
    const currentSceneHeight = (isTopCutDirection && index === countScenes) || (!isTopCutDirection && index === 1)
      ? lastSceneHeight
      : sceneHeight;
    accumulator = accumulator + currentSceneHeight;
    distances.push(accumulator);
  }
  return distances;
};

const calculateCountScenesAndDistanceParameters = (
  sceneHeight: number,
  northDistance: number,
  centerDistance: number,
  southDistance: number,
  productImageHeight: number,
) => {
  const parameters = {
    countScenes: 0,
    startBottomDistance: 0,
    sceneHeight
  };

  if (southDistance + northDistance < 0.5) {
    if (centerDistance % sceneHeight >= sceneHeight/2) {
      parameters.countScenes = _.ceil(centerDistance / sceneHeight);
    } else {
      parameters.countScenes = _.floor(centerDistance / sceneHeight) > 0
        ? _.floor(centerDistance / sceneHeight)
        : 1;
      parameters.sceneHeight = productImageHeight / parameters.countScenes;
    }
  } else {
    let insideSceneHeightLeft;
    if (centerDistance / sceneHeight > 1) {
      insideSceneHeightLeft = centerDistance % sceneHeight;
      parameters.countScenes = _.floor(centerDistance / sceneHeight);
    } else {
      insideSceneHeightLeft = centerDistance;
    }

    let deltaSouth = 0;
    let deltaNorth = 0;
    const remainSpaceHeight = insideSceneHeightLeft + southDistance + northDistance;
    const canCreateAnotherScene = insideSceneHeightLeft > 0 && remainSpaceHeight >= sceneHeight/2;
    if (canCreateAnotherScene) {
      const outsideSceneHeightLeft = Math.min(remainSpaceHeight, sceneHeight) - insideSceneHeightLeft;
      // there is enough space to add an extra scene
      parameters.countScenes = parameters.countScenes + 1;
      if (isBottomToTopCutDirection(southDistance, northDistance)) {
        // north cut direction
        deltaSouth = Math.min(southDistance, outsideSceneHeightLeft / 2);
        deltaNorth = Math.min(northDistance, outsideSceneHeightLeft - deltaSouth);
      } else {
        // south cut direction
        deltaNorth = Math.min(northDistance, outsideSceneHeightLeft / 2);
        deltaSouth = Math.min(southDistance, outsideSceneHeightLeft - deltaNorth);
      }
    } else {
      deltaSouth = (parameters.countScenes * sceneHeight - centerDistance) / 2;
      deltaNorth = deltaSouth;
    }

    // don't waste space
    const remainSpaceHeightAfterPartition = (southDistance - deltaSouth) + (northDistance - deltaNorth);
    if ( remainSpaceHeightAfterPartition > 0 && remainSpaceHeightAfterPartition < sceneHeight / 2) {
      deltaSouth = southDistance;
      parameters.sceneHeight = productImageHeight / parameters.countScenes;
    }

    parameters.startBottomDistance = southDistance - deltaSouth;
  }

  return parameters;
};

const isBottomToTopCutDirection = (
  southDistance: number,
  northDistance: number
): boolean => {
  return southDistance <= northDistance;
};

const calculateDistancesFromBottomSide = (
  productImageFeature,
  intersectionFeature,
  sceneHeight
): number[] => {
  const {
    northDistance,
    centerDistance,
    southDistance,
    productImageHeight
  } = calculateNorthSouthCenterDistances(productImageFeature, intersectionFeature);

  const {
    countScenes,
    startBottomDistance,
    sceneHeight: calculatedSceneHeight
  } = calculateCountScenesAndDistanceParameters(sceneHeight, northDistance, centerDistance, southDistance, productImageHeight);

  return getDistances(startBottomDistance, countScenes, calculatedSceneHeight, productImageHeight, isBottomToTopCutDirection(southDistance, northDistance));
};

const rotateScenesInformation = (
  scenesInformation: ScenesInformationType,
  bearingAngle: number,
  centerPoint: PointFeatureType
): ScenesInformationType  => {
  const rotatedScenesInformation: ScenesInformationType = {
    scenes: []
  };

  rotatedScenesInformation.scenes = scenesInformation.scenes.map((scene: ShapeFeatureType) => {
    return getRotationFeature(scene, bearingAngle, centerPoint);
  });

  if (scenesInformation.sceneCorners) {
    rotatedScenesInformation.sceneCorners = {
      bottomLeft: getPointRotationPosition(scenesInformation.sceneCorners.bottomLeft, bearingAngle, centerPoint),
      bottomRight: getPointRotationPosition(scenesInformation.sceneCorners.bottomRight, bearingAngle, centerPoint),
      upLeft: getPointRotationPosition(scenesInformation.sceneCorners.upLeft, bearingAngle, centerPoint),
      upRight: getPointRotationPosition(scenesInformation.sceneCorners.upRight, bearingAngle, centerPoint),
    }
  }

  return rotatedScenesInformation;
};

export const getScenesInformationFrom = (
    selectedArea: ShapeType,
    productImage: ProductImageStacType
): Promise<any> => {
  return GeoCodingService.framing(selectedArea, productImage);
}

export const extractScenesInformationFrom = (
  selectedArea: ShapeType,
  productImage: ProductImageStacType
): ScenesInformationType => {
  let scenesInformation: ScenesInformationType = {
    scenes: []
  };

  try {
    const quadrilateralProductImage = convertToQuadrilateralGeoJSONFrom(productImage) as ProductImageStacType;

    const sceneHeight = calculateSceneHeightBy(quadrilateralProductImage);

    const intersectionFeature = getIntersectionFeatureBetweenSelectedAreaAndProductImage(selectedArea, quadrilateralProductImage);

    const intersectionCenterPointFeature = getCenterPointFeature(intersectionFeature);

    const sceneBearingAngle = calculateProductImageBearingAngle(quadrilateralProductImage);

    const rotatedProductImageFeature = getRotationFeature(quadrilateralProductImage as ShapeFeatureType, -sceneBearingAngle, intersectionCenterPointFeature);

    const rotatedIntersectionFeature = getRotationFeature(intersectionFeature, -sceneBearingAngle, intersectionCenterPointFeature);

    const rotatedScenesInformation = calculateScenesInformationFromBottomSideBy(rotatedProductImageFeature, rotatedIntersectionFeature, sceneHeight);

    scenesInformation = rotateScenesInformation(rotatedScenesInformation, sceneBearingAngle, intersectionCenterPointFeature);

  } catch (e) {
    console.warn(`Cut Scene: ${e.message}`, productImage);
  }


  return scenesInformation;
};
